/**
 * Categories Module
 *
 * Handles category-specific functionality for index, new, and edit pages.
 * Auto-initializes based on page context.
 *
 * Usage in Twig:
 * Index: <div data-controller="categories-index" data-base-url="/categories">
 * Form: <form id="category-form" data-controller="categories-form" data-category-id="123">
 */

import { $, $$, on, ready } from '../utils/dom.js';
import { getToken, getHeaders } from '../utils/csrf.js';
import { showNotification } from './notifications.js';

// ============================================================================
// Category Index Page
// ============================================================================

/**
 * Apply status filter
 * @param {HTMLSelectElement} select - The status select element
 * @param {string} baseUrl - Base URL for categories (default: /categories)
 */
export function applyStatusFilter(select, baseUrl = '/categories') {
    const status = select.value;
    const urlParams = new URLSearchParams(window.location.search);

    if (status) {
        urlParams.set('status', status);
    } else {
        urlParams.delete('status');
    }

    window.location.href = baseUrl + (urlParams.toString() ? '?' + urlParams.toString() : '');
}

/**
 * Apply parent filter
 * @param {HTMLSelectElement} select - The parent select element
 * @param {string} baseUrl - Base URL for categories (default: /categories)
 */
export function applyParentFilter(select, baseUrl = '/categories') {
    const parent = select.value;
    const urlParams = new URLSearchParams(window.location.search);

    if (parent) {
        urlParams.set('parent', parent);
    } else {
        urlParams.delete('parent');
    }

    window.location.href = baseUrl + (urlParams.toString() ? '?' + urlParams.toString() : '');
}

/**
 * Clear all filters
 * @param {string} baseUrl - Base URL for categories (default: /categories)
 */
export function clearFilters(baseUrl = '/categories') {
    window.location.href = baseUrl;
}

/**
 * Duplicate a category via AJAX
 * @param {string} id - Category ID
 * @param {string} name - Category name (for confirmation)
 */
export function duplicateCategory(id, name) {
    if (!confirm(`Are you sure you want to duplicate the category "${name}"?`)) {
        return;
    }

    showNotification('Duplicating category...', 'info');

    fetch(`/categories/${id}/duplicate`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            _token: getToken()
        })
    })
    .then(response => {
        if (!response.ok) {
            return response.json().then(err => {
                throw new Error(err.message || 'Failed to duplicate category');
            });
        }
        return response.json();
    })
    .then(data => {
        if (data.success) {
            showNotification(data.message || 'Category duplicated successfully', 'success');

            if (data.redirect) {
                setTimeout(() => window.location.href = data.redirect, 1000);
            } else {
                setTimeout(() => window.location.reload(), 1000);
            }
        } else {
            throw new Error(data.message || 'Failed to duplicate category');
        }
    })
    .catch(error => {
        console.error('Error:', error);
        showNotification(error.message || 'An error occurred while duplicating the category', 'error');
    });
}

/**
 * Delete a category via AJAX
 * @param {string} id - Category ID
 * @param {string} name - Category name (for confirmation)
 */
export function deleteCategory(id, name) {
    const confirmMsg = name
        ? `Are you sure you want to delete the category "${name}"?\n\nThis action cannot be undone.`
        : 'Are you sure you want to delete this category?\n\nThis action cannot be undone.';

    if (!confirm(confirmMsg)) {
        return;
    }

    const formData = new FormData();
    formData.append('action', 'delete_category');
    formData.append('category_id', id);
    if (window.ajaxNonce) {
        formData.append('_ajax_nonce', window.ajaxNonce);
    }

    fetch('/admin-ajax.php', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        body: formData
    })
    .then(response => response.json())
    .then(data => {
        if (data.success) {
            window.location.href = '/categories';
        } else {
            showNotification(data.message || 'Failed to delete category', 'error');
        }
    })
    .catch(error => {
        console.error('Error:', error);
        showNotification('An error occurred while deleting the category', 'error');
    });
}

/**
 * Execute bulk action on categories
 * @param {string} action - Action to perform (activate, deactivate, delete)
 * @param {string[]} ids - Array of category IDs
 * @param {HTMLElement} [button] - Optional button element to show loading state
 */
export function executeBulkAction(action, ids, button = null) {
    const originalHtml = button?.innerHTML;

    if (button) {
        button.disabled = true;
        button.innerHTML = '<svg class="animate-spin h-4 w-4 mr-2 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Processing...';
    }

    fetch('/categories/bulk', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            _token: getToken(),
            action: action,
            ids: ids
        })
    })
    .then(response => {
        if (!response.ok) {
            return response.json().then(err => {
                throw new Error(err.message || 'Failed to perform bulk action');
            });
        }
        return response.json();
    })
    .then(data => {
        if (data.success) {
            showNotification(data.message || 'Bulk action completed successfully', 'success');
            setTimeout(() => window.location.reload(), 1000);
        } else {
            throw new Error(data.message || 'Bulk action failed');
        }
    })
    .catch(error => {
        console.error('Bulk action error:', error);
        showNotification(error.message || 'An error occurred while performing the bulk action', 'error');
        if (button) {
            button.disabled = false;
            button.innerHTML = originalHtml;
        }
    });
}

/**
 * Initialize categories index page functionality
 * @param {HTMLElement} container - The container element
 */
function initCategoriesIndex(container) {
    const baseUrl = container.dataset.baseUrl || '/categories';
    const selectAllCheckbox = $('#select-all', container);
    const bulkActionSelect = $('#bulk-action-select', container);
    const applyBulkActionBtn = $('#apply-bulk-action', container);

    function getRowCheckboxes() {
        return $$('.row-checkbox', container);
    }

    function updateSelectAllState() {
        if (!selectAllCheckbox) return;

        const checkboxes = getRowCheckboxes();
        const checkedCount = checkboxes.filter(cb => cb.checked).length;

        selectAllCheckbox.checked = checkboxes.length > 0 && checkedCount === checkboxes.length;
        selectAllCheckbox.indeterminate = checkedCount > 0 && checkedCount < checkboxes.length;
    }

    function updateBulkActionButton() {
        const checkedCount = getRowCheckboxes().filter(cb => cb.checked).length;
        if (applyBulkActionBtn) {
            applyBulkActionBtn.disabled = checkedCount === 0;
            applyBulkActionBtn.classList.toggle('opacity-50', checkedCount === 0);
            applyBulkActionBtn.classList.toggle('cursor-not-allowed', checkedCount === 0);
        }
    }

    // Select/Deselect All
    if (selectAllCheckbox) {
        selectAllCheckbox.addEventListener('change', function() {
            getRowCheckboxes().forEach(cb => cb.checked = this.checked);
            updateBulkActionButton();
        });
    }

    // Update states when individual checkboxes change
    on(container, 'change', '.row-checkbox', () => {
        updateSelectAllState();
        updateBulkActionButton();
    });

    // Apply Bulk Action
    if (applyBulkActionBtn) {
        applyBulkActionBtn.addEventListener('click', function() {
            const selectedAction = bulkActionSelect?.value || '';
            const checkedCheckboxes = getRowCheckboxes().filter(cb => cb.checked);

            if (!selectedAction) {
                showNotification('Please select an action', 'error');
                return;
            }

            if (checkedCheckboxes.length === 0) {
                showNotification('Please select at least one category', 'error');
                return;
            }

            const selectedIds = checkedCheckboxes.map(cb => cb.value);
            const selectedNames = checkedCheckboxes.map(cb => cb.getAttribute('data-name'));

            const actionText = {
                'activate': 'activate',
                'deactivate': 'deactivate',
                'delete': 'delete'
            }[selectedAction];

            const confirmMessage = `Are you sure you want to ${actionText} ${selectedIds.length} categor${selectedIds.length === 1 ? 'y' : 'ies'}?`;

            if (selectedAction === 'delete') {
                const categoryNames = selectedNames.join(', ');
                if (!confirm(`${confirmMessage}\n\nCategories: ${categoryNames}\n\nThis action cannot be undone.`)) {
                    return;
                }
            } else if (!confirm(confirmMessage)) {
                return;
            }

            executeBulkAction(selectedAction, selectedIds, this);
        });
    }

    // Initial state
    updateBulkActionButton();
}

// ============================================================================
// Category Form (New/Edit)
// ============================================================================

/**
 * Initialize category form functionality
 * @param {HTMLFormElement} form - The form element
 */
function initCategoryForm(form) {
    let categoryId = form.dataset.categoryId;
    if (!categoryId) {
        const container = $('#category-products', form);
        categoryId = container?.dataset?.categoryId;
    }

    // Form validation
    form.addEventListener('submit', function(e) {
        const nameInput = $('#name', form);
        const name = nameInput?.value.trim();

        if (!name) {
            alert('Please enter a category name');
            nameInput?.focus();
            e.preventDefault();
            return;
        }
    });

    if (categoryId) {
        initCategoryProductAssignments(form, categoryId);
    }
}

function initCategoryProductAssignments(form, categoryId) {
    const container = $('#category-products', form);
    if (!container) {
        return;
    }

    const searchInput = $('#category-product-search', container);
    const listEl = $('#category-products-list', container);
    const countEl = $('#category-products-count', container);
    const selectAll = $('#category-products-select-all', container);
    const assignBtn = $('#category-products-assign', container);
    const removeBtn = $('#category-products-remove', container);

    let products = [];
    let filtered = [];

    const formatPrice = (price) => (price === null || price === undefined)
        ? '—'
        : `$${Number(price).toFixed(2)}`;

    function renderList(items) {
        if (!listEl) return;
        if (items.length === 0) {
            listEl.innerHTML = '<div class="p-4 text-sm text-zinc-500">No products found.</div>';
            return;
        }

        listEl.innerHTML = items.map(product => {
            const sku = product.sku ? `<div class="text-xs text-zinc-400">SKU: ${product.sku}</div>` : '';
            const statusLabel = product.assigned ? 'In Category' : 'Other';
            const statusClass = product.assigned ? 'text-green-700 bg-green-100' : 'text-zinc-600 bg-zinc-100';
            return `
                <label class="flex items-center gap-3 px-3 py-2 border-b border-zinc-100 hover:bg-zinc-50 text-sm">
                    <input type="checkbox" class="category-product-checkbox h-4 w-4 text-blue-600 border-zinc-300 rounded" data-id="${product.id}">
                    <div class="flex-1 min-w-0">
                        <div class="font-medium text-zinc-900 truncate">${product.name}</div>
                        ${sku}
                    </div>
                    <div class="w-24 text-right text-zinc-700">${formatPrice(product.price)}</div>
                    <div class="w-28 text-right">
                        <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusClass}">${statusLabel}</span>
                    </div>
                </label>
            `;
        }).join('');
    }

    function updateCounts() {
        if (!countEl) return;
        const assignedCount = products.filter(p => p.assigned).length;
        countEl.textContent = `${products.length} products, ${assignedCount} assigned`;
    }

    function getVisibleCheckboxes() {
        return $$('.category-product-checkbox', listEl);
    }

    function updateSelectAllState() {
        if (!selectAll) return;
        const boxes = getVisibleCheckboxes();
        const checked = boxes.filter(cb => cb.checked).length;
        selectAll.checked = boxes.length > 0 && checked === boxes.length;
        selectAll.indeterminate = checked > 0 && checked < boxes.length;
    }

    function updateActionButtons() {
        const selectedCount = getVisibleCheckboxes().filter(cb => cb.checked).length;
        if (assignBtn) assignBtn.disabled = selectedCount === 0;
        if (removeBtn) removeBtn.disabled = selectedCount === 0;
    }

    function applyFilter() {
        const query = (searchInput?.value || '').trim().toLowerCase();
        if (!query) {
            filtered = products;
        } else {
            filtered = products.filter(product => {
                const nameMatch = product.name.toLowerCase().includes(query);
                const skuMatch = (product.sku || '').toLowerCase().includes(query);
                return nameMatch || skuMatch;
            });
        }
        renderList(filtered);
        updateSelectAllState();
        updateActionButtons();
    }

    async function fetchProducts() {
        listEl.innerHTML = '<div class="p-4 text-sm text-zinc-500">Loading products...</div>';
        const formData = new FormData();
        formData.append('action', 'get_category_products');
        formData.append('category_id', categoryId);
        if (window.ajaxNonce) {
            formData.append('_ajax_nonce', window.ajaxNonce);
        }

        try {
            const response = await fetch('/admin-ajax.php', {
                method: 'POST',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
                credentials: 'same-origin',
                body: formData
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to load products');
            }
            products = data.products || [];
            updateCounts();
            applyFilter();
        } catch (error) {
            console.error(error);
            listEl.innerHTML = `<div class="p-4 text-sm text-red-600">Unable to load products. ${error.message || ''}</div>`;
            showNotification(error.message || 'Failed to load products', 'error');
        }
    }

    async function postSelection(action) {
        const selectedIds = getVisibleCheckboxes()
            .filter(cb => cb.checked)
            .map(cb => cb.getAttribute('data-id'));

        if (selectedIds.length === 0) {
            showNotification('Select at least one product', 'error');
            return;
        }

        const formData = new FormData();
        formData.append('action', action);
        formData.append('category_id', categoryId);
        selectedIds.forEach(id => formData.append('product_ids[]', id));
        if (window.ajaxNonce) {
            formData.append('_ajax_nonce', window.ajaxNonce);
        }

        try {
            const response = await fetch('/admin-ajax.php', {
                method: 'POST',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
                credentials: 'same-origin',
                body: formData
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Update failed');
            }
            showNotification(data.message || 'Products updated', 'success');
            await fetchProducts();
        } catch (error) {
            console.error(error);
            showNotification(error.message || 'Failed to update products', 'error');
        }
    }

    if (searchInput) {
        searchInput.addEventListener('input', applyFilter);
    }

    if (selectAll) {
        selectAll.addEventListener('change', () => {
            getVisibleCheckboxes().forEach(cb => {
                cb.checked = selectAll.checked;
            });
            updateSelectAllState();
            updateActionButtons();
        });
    }

    if (listEl) {
        listEl.addEventListener('change', (event) => {
            if (event.target && event.target.classList.contains('category-product-checkbox')) {
                updateSelectAllState();
                updateActionButtons();
            }
        });
    }

    if (assignBtn) {
        assignBtn.addEventListener('click', () => postSelection('assign_products_to_category'));
    }

    if (removeBtn) {
        removeBtn.addEventListener('click', () => postSelection('unassign_products_from_category'));
    }

    fetchProducts();
}

// ============================================================================
// Auto-initialization
// ============================================================================

ready(() => {
    // Initialize categories index pages
    $$('[data-controller="categories-index"]').forEach(initCategoriesIndex);

    // Initialize category forms
    $$('[data-controller="categories-form"], #category-form').forEach(form => {
        if (form.tagName === 'FORM') {
            initCategoryForm(form);
        }
    });
});

// ============================================================================
// Expose globally for legacy onclick handlers
// ============================================================================

window.duplicateCategory = duplicateCategory;
window.deleteCategory = deleteCategory;

// Override the generic filter functions for category-specific behavior
// These can be called directly from onclick handlers
window.applyParentFilter = function(select) {
    applyParentFilter(select, '/categories');
};

export default {
    applyStatusFilter,
    applyParentFilter,
    clearFilters,
    duplicateCategory,
    deleteCategory,
    executeBulkAction
};
